import type { RoundName } from "../../domain/round/types";

const API_BASE = String(import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/$/, "");

export type CarBlockPerson = { id: string; name: string };
export type CarBlockRow = {
  id: string;
  number: number;
  vehicleId: string;
  tripNumber: number;
  departAtMinutes: number;
  startIndex: number;
  endIndex: number;
  contiguous: boolean;
  people: CarBlockPerson[];
};
export type CarBlocksPayload = {
  ok: boolean;
  roundName: RoundName;
  revision: number;
  confirmed: boolean;
  blocks: CarBlockRow[];
};
export type CarBlockReorderPayload = {
  expectedRevision: number;
  blockNumber: number;
  targetBlockNumber?: number;
  direction?: "up" | "down";
};
export type CarBlockReorderResult = {
  ok: boolean;
  revision: number;
  requiresConfirmation: boolean;
  changedPersonIds?: string[];
  blocks: CarBlockRow[];
};

function errorDetail(body: unknown) {
  if (!body || typeof body !== "object" || !("detail" in body)) return "";
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "message" in detail) {
    return String((detail as { message?: unknown }).message || "");
  }
  return "";
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  const contentType = response.headers.get("content-type") || "";
  const body = contentType.includes("application/json")
    ? await response.json().catch(() => null)
    : await response.text().catch(() => "");
  if (!response.ok) throw new Error(errorDetail(body) || `HTTP ${response.status}`);
  return body as T;
}

export const carBlockApi = {
  list: (tournamentId: string, round: RoundName) =>
    request<CarBlocksPayload>(
      `/tournaments/${encodeURIComponent(tournamentId)}/rounds/${round}/car-blocks`,
    ),
  reorder: (tournamentId: string, round: RoundName, payload: CarBlockReorderPayload) =>
    request<CarBlockReorderResult>(
      `/tournaments/${encodeURIComponent(tournamentId)}/rounds/${round}/car-blocks/reorder`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
};
